import { useState, useCallback, useMemo } from "react";
import { ArrowDownCircle, Edit, Receipt, Eye } from "lucide-react";
import styles from "./IncomingPayments.module.css";
import ViewPaymentModal from "../../../../components/Finance/Payments/ViewPaymentModal";
import SelectBox from "../../../../components/BaseComponents/SelectBox/SelectBox";
import RegisterPaymentModal from "../../../../components/Finance/Payments/RegisterPaymentModal/RegisterPaymentModal";
import GenerateReceiptModal from "../../../../components/Finance/Payments/GenerateReceiptModal";

const IncomingPayments = () => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);

  // Sample data - replace with API hook
  const [payments] = useState([
    {
      id: 1,
      bookingRef: "BK-2024-001234",
      customerName: "Sarah Johnson",
      customerEmail: "sarah.j@email.com",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "Bank Transfer",
      amount: 1240.00,
      currency: "EUR",
      date: "2024-03-10",
      status: "Completed",
      reference: "PAY-IN-20240310-001",
      notes: "Full payment received"
    },
    {
      id: 2,
      bookingRef: "BK-2024-001235",
      customerName: "Michael Chen",
      customerEmail: "m.chen@company.com",
      account: "Credit Card Processing",
      accountId: 3,
      paymentMethod: "Visa",
      amount: 950.00,
      currency: "EUR",
      date: "2024-03-09",
      status: "Pending",
      reference: "PAY-IN-20240309-005",
      notes: "Awaiting bank confirmation"
    },
    {
      id: 3,
      bookingRef: "BK-2024-001220",
      customerName: "Emma Wilson",
      customerEmail: "emma.w@email.com",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "SEPA",
      amount: 2150.00,
      currency: "EUR",
      date: "2024-03-08",
      status: "Completed",
      reference: "PAY-IN-20240308-012",
      notes: "Deposit payment"
    },
    {
      id: 4,
      bookingRef: "BK-2024-001198",
      customerName: "James Brown",
      customerEmail: "j.brown@email.com",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "Bank Transfer",
      amount: 675.00,
      currency: "EUR",
      date: "2024-03-07",
      status: "Completed",
      reference: "PAY-IN-20240307-008",
      notes: "Balance payment"
    },
    {
      id: 5,
      bookingRef: "BK-2024-001187",
      customerName: "Lisa Anderson",
      customerEmail: "lisa.a@email.com",
      account: "Credit Card Processing",
      accountId: 3,
      paymentMethod: "Mastercard",
      amount: 1850.00,
      currency: "EUR",
      date: "2024-03-06",
      status: "Failed",
      reference: "PAY-IN-20240306-003",
      notes: "Insufficient funds"
    },
    {
      id: 6,
      bookingRef: "BK-2024-001156",
      customerName: "Robert Taylor",
      customerEmail: "r.taylor@company.com",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "Bank Transfer",
      amount: 3240.00,
      currency: "EUR",
      date: "2024-03-05",
      status: "Completed",
      reference: "PAY-IN-20240305-015",
      notes: "Full payment for group booking"
    }
  ]);

  const loading = false;
  const error = null;
  const saveLoading = false;

  const getStatusBadgeClass = (status) => {
    const baseClass = styles.statusBadge;
    switch (status) {
      case "Completed":
        return `${baseClass} ${styles.statusCompleted}`;
      case "Pending":
        return `${baseClass} ${styles.statusPending}`;
      case "Failed":
        return `${baseClass} ${styles.statusFailed}`;
      default:
        return baseClass;
    }
  };

  const filteredRows = useMemo(() => {
    return payments.filter((row) => {
      const matchesSearch = search === "" || 
        row.bookingRef.toLowerCase().includes(search.toLowerCase()) ||
        row.customerName.toLowerCase().includes(search.toLowerCase()) ||
        row.reference.toLowerCase().includes(search.toLowerCase());
      
      const matchesStatus = statusFilter === "" || row.status === statusFilter;
      const matchesMethod = methodFilter === "" || row.paymentMethod === methodFilter;
      
      const matchesDateFrom = dateFrom === "" || row.date >= dateFrom;
      const matchesDateTo = dateTo === "" || row.date <= dateTo;
      
      return matchesSearch && matchesStatus && matchesMethod && matchesDateFrom && matchesDateTo;
    });
  }, [payments, search, statusFilter, methodFilter, dateFrom, dateTo]);

  const totalAmount = filteredRows.reduce((sum, payment) => sum + payment.amount, 0);
  const completedAmount = filteredRows
    .filter(p => p.status === "Completed")
    .reduce((sum, payment) => sum + payment.amount, 0);
  const pendingAmount = filteredRows
    .filter(p => p.status === "Pending")
    .reduce((sum, payment) => sum + payment.amount, 0);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleRegister = () => {
    setMode("add");
    setSelectedRow(null);
    setShowRegisterModal(true);
  };

  const handleEdit = (row) => {
    setMode("edit");
    setSelectedRow(row);
    setShowRegisterModal(true);
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setShowViewModal(true);
  };

  const handleSave = async (payload) => {
    try {
      if (mode === "add") {
        console.log("Register payment:", payload);
        // await registerPayment(payload);
      } else if (selectedRow?.id) {
        console.log("Update payment:", selectedRow.id, payload);
        // await updatePayment(selectedRow.id, payload);
      }
      setShowRegisterModal(false);
      // showToast("Payment saved successfully", "success");
      // refetch();
    } catch (err) {
      console.error("Failed to save payment:", err);
      // showToast("Failed to save payment", "error");
    }
  };

  const handleGenerateReceipt = (payment) => {
    setSelectedRow(payment);
    setShowReceiptModal(true);
  };

  const handleGenerateReceiptSubmit = async (receiptData) => {
    try {
      console.log("Generating receipt with data:", receiptData);
      
      // API call to generate receipt
      // const response = await generateReceipt(receiptData);
      
      // If sendEmail is true, also send email
      // if (receiptData.sendEmail) {
      //   await sendReceiptEmail(receiptData);
      // }
      
      // showToast("Receipt generated successfully", "success");
      
      // Optionally download the PDF
      // window.open(response.pdfUrl, '_blank');
      
    } catch (err) {
      console.error("Failed to generate receipt:", err);
      // showToast("Failed to generate receipt", "error");
      throw err; // Re-throw to let modal handle it
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Incoming Payments (Client)</div>
        <button className={styles.add} onClick={handleRegister}>
          + Register Payment
        </button>
      </div>

      <div className={styles.infoBar}>
        <span className={styles.infoIcon} aria-hidden>ℹ️</span>
        <span>Incoming payments from clients. Supports online, manual, partial, and full payments.</span>
      </div>

      {/* Summary Cards */}
      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Total Received</div>
          <div className={styles.summaryValue}>€{completedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className={styles.summaryCount}>{filteredRows.filter(p => p.status === "Completed").length} payments</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Pending</div>
          <div className={styles.summaryValue} style={{color: '#f59e0b'}}>€{pendingAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className={styles.summaryCount}>{filteredRows.filter(p => p.status === "Pending").length} payments</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Failed</div>
          <div className={styles.summaryValue} style={{color: '#ef4444'}}>
            €{filteredRows.filter(p => p.status === "Failed").reduce((sum, p) => sum + p.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className={styles.summaryCount}>{filteredRows.filter(p => p.status === "Failed").length} payments</div>
        </div>
      </div>

      <div className={styles.filterBar}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search by booking, customer, or reference..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.addbtn}>
            Search
          </button>
        </form>

        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className={styles.dateInput}
          placeholder="From"
        />

        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className={styles.dateInput}
          placeholder="To"
        />

        <SelectBox
          placeholder="Method: All"
          value={methodFilter}
          options={[
            { label: "All Methods", value: "" },
            { label: "Bank Transfer", value: "Bank Transfer" },
            { label: "SEPA", value: "SEPA" },
            { label: "Visa", value: "Visa" },
            { label: "Mastercard", value: "Mastercard" },
          ]}
          onChange={(e) => setMethodFilter(e.target.value)}
        />

        <SelectBox
          placeholder="Status: All"
          value={statusFilter}
          options={[
            { label: "All Status", value: "" },
            { label: "Completed", value: "Completed" },
            { label: "Pending", value: "Pending" },
            { label: "Failed", value: "Failed" },
          ]}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading payments...</div>
      ) : (
        <>
          <div className={styles.meta}>
            Showing {filteredRows.length} of {payments.length} payments
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>BOOKING REF</th>
                  <th>CUSTOMER</th>
                  <th>PAYMENT METHOD</th>
                  <th>ACCOUNT</th>
                  <th>AMOUNT</th>
                  <th>REFERENCE</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td className={styles.date}>{row.date}</td>
                    <td>
                      <span className={styles.bookingRef}>{row.bookingRef}</span>
                    </td>
                    <td>
                      <div className={styles.customerInfo}>
                        <div className={styles.customerName}>{row.customerName}</div>
                        <div className={styles.customerEmail}>{row.customerEmail}</div>
                      </div>
                    </td>
                    <td>
                      <span className={styles.badge}>{row.paymentMethod}</span>
                    </td>
                    <td className={styles.accountName}>{row.account}</td>
                    <td className={styles.amount}>
                      €{row.amount.toLocaleString('en-US', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}
                    </td>
                    <td className={styles.reference}>{row.reference}</td>
                    <td>
                      <span className={getStatusBadgeClass(row.status)}>
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleView(row)}
                          title="View payment"
                        >
                          <Eye size={16} />
                        </button>
                        {row.status !== "Completed" && (
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleEdit(row)}
                            title="Edit payment"
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        {row.status === "Completed" && (
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleGenerateReceipt(row)}
                            title="Generate receipt"
                          >
                            <Receipt size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <RegisterPaymentModal
        open={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        mode={mode}
        initialData={selectedRow}
        onSave={handleSave}
        saveLoading={saveLoading}
        paymentType="incoming"
      />

      <ViewPaymentModal
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        payment={selectedRow}
        paymentType="incoming"
      />

      <GenerateReceiptModal
        open={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        payment={selectedRow}
        onGenerate={handleGenerateReceiptSubmit}
      />
    </div>
  );
};

export default IncomingPayments;